import React, { useEffect, useState, useCallback } from "react";
import { Base_URL } from "../../url";
import "../../Styling/hrDashboard.css";
import { Eye, EyeOff, Download, Users, TrendingUp, Calendar, FileText, Activity } from "lucide-react";
import { useAuth } from "../../Context/AuthContext";
import { useNavigate } from "react-router-dom";

const HrDashboardDropdownDynamic = ({ }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgIds, setOrgIds] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  
  
  const [filters, setFilters] = useState({
    orgId: String(user?.organisationId || ""),
    companyCode: "",
    fiscalYear: "All Fiscal Years",
    postingDate: new Date().toISOString().split('T')[0]
  });

  // ✅ State for filter display names
  const [filterDisplay, setFilterDisplay] = useState({
    orgName: '',
    companyName: ''
  });

  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [employeeCounts, setEmployeeCounts] = useState({
    permanent: 0,
    dummy: 0,
    intern: 0,
    partial: 0,
    total: 0
  });
  const [companySearch, setCompanySearch] = useState("");
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  
  const [salaryData, setSalaryData] = useState({
    totalSalary: 0,
    totalPaid: 0,
    totalPending: 0
  });
  
  const [attendanceData, setAttendanceData] = useState({
    present: 0,
    wfh: 0,
    leave: 0,
    total: 0
  });

  const [realAttendanceData, setRealAttendanceData] = useState([]);
  
  const [plannedLeaveData, setPlannedLeaveData] = useState([]);
  const [employeeTrendData, setEmployeeTrendData] = useState([]);
  const [hiringExitData, setHiringExitData] = useState([]);

  
  const [chartVisibility, setChartVisibility] = useState({
    attendanceChart: true,
    plannedLeaves: true,
    employeeTrend: true,
    hiringExit: true,
    presentEmployees: true,
    onTimePerformance: true,
    employeeDelay: true,
    birthdayList: true
  });

  const [chartDataVisibility, setChartDataVisibility] = useState({
    attendanceChart: true,
    plannedLeaves: true,
    employeeTrend: true,
    hiringExit: true,
    presentEmployees: true,
    onTimePerformance: true,
    employeeDelay: true,
    birthdayList: true 
  });

  const toggleChartVisibility = (chartName) => {
    setChartVisibility(prev => ({
      ...prev,
      [chartName]: !prev[chartName]
    }));
  };

  const toggleChartDataVisibility = (chartName) => {
    setChartDataVisibility(prev => ({
      ...prev,
      [chartName]: !prev[chartName]
    }));
  };

  const anyChartVisible = Object.values(chartVisibility).some(v => v);

  const fetchOrgIds = useCallback(async () => {
    try {
      if (!user?.userId) return;

      const res = await fetch(`${Base_URL}/HrDashboard/organisations`);
      if (!res.ok) throw new Error(`Failed to fetch organization data: ${res.status}`);

      const data = await res.json();

      const orgList = Array.isArray(data)
        ? data.map(item => ({
            id: item.organisation_Id || item.Organisation_Id,
            name: item.organisation_Name || item.Organisation_Name,
          }))
        : [];

      const uniqueOrgList = orgList.filter(
        (v, i, self) =>
          i === self.findIndex(t => t.id === v.id)
      );

      setOrgIds(uniqueOrgList);

      // ✅ Auto-select user's organization if available
      if (user?.organisationId && uniqueOrgList.length > 0) {
        const userOrg = uniqueOrgList.find(org => org.id == user.organisationId);
        if (userOrg) {
          setFilters(prev => ({
            ...prev,
            orgId: String(user.organisationId)
          }));
          
          setFilterDisplay(prev => ({
            ...prev,
            orgName: userOrg.name
          }));
          
          // Auto-populate companies for user's organization
          fetchCompanyCodes(String(user.organisationId));
        }
      }
    } catch (err) {
      console.error("Error fetching org IDs:", err);
      setError(err.message || "Failed to load Organization IDs");
      setOrgIds([]);
    }
  }, [user]);

  // ✅ Fetch Company Codes
  const fetchCompanyCodes = useCallback(async (orgId) => {
    try {
      if (!orgId) {
        setCompanyCodes([]);
        setFilters(prev => ({ ...prev, companyCode: "" }));
        return;
      }

      const res = await fetch(`${Base_URL}/HrDashboard/companycodes/${orgId}`);
      
      if (!res.ok) throw new Error(`Failed to fetch company codes: ${res.status}`);

      const data = await res.json();
      console.log(data);

      let companies = Array.isArray(data)
        ? data.map(c => ({
            code: c.CompanyCode || c.companyCode,
            name: c.Company_Name || c.company_Name || ""
          }))
        : [];

      const uniqueCompanies = companies.filter(
        (v, i, self) =>
          i === self.findIndex(t => t.code === v.code)
      );

      setCompanyCodes(uniqueCompanies);
    } catch (err) {
      console.error("Error fetching company codes:", err);
      setCompanyCodes([]);
    }
  }, []);

  // ✅ Fetch Fiscal Years
  const fetchFiscalYears = useCallback(async () => {
    try {
      const res = await fetch(`${Base_URL}/HrDashboard/financialyears`);
      if (!res.ok) throw new Error("Failed to fetch fiscal years");
      const data = await res.json();
      setFiscalYears(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching fiscal years:", err);
      setFiscalYears([]);
    }
  }, []);

  // ✅ Fetch Employee Types
  const fetchEmployeeTypes = useCallback(async (orgId, companyCode = "", fiscalYear = "All Fiscal Years") => {
    if (!orgId) return;
    
    setLoading(true);
    setError("");
    
    try {
      const params = new URLSearchParams();
      params.append('orgId', orgId);
      
      if (companyCode && companyCode !== "") {
        params.append('companyCode', companyCode);
      }
      
      if (fiscalYear && fiscalYear !== "All Fiscal Years") {
        params.append('fiscalYear', fiscalYear);
      }

      const url = `${Base_URL}/HrDashboard/filtered?${params.toString()}`;
      console.log("Fetching from:", url);

      const res = await fetch(url);
      
      if (!res.ok) {
        if (res.status === 404) {
          setEmployeeCounts({
            permanent: 0,
            dummy: 0,
            intern: 0,
            partial: 0,
            total: 0
          });
          setEmployeeTypes([]);
          return;
        }
        throw new Error(`Failed to fetch employee types: ${res.status} ${res.statusText}`);
      }
      
      const data = await res.json();
      processEmployeeCounts(data);
      
    } catch (err) {
      console.error("Error fetching employee types:", err);
      setError(err.message || "Failed to fetch employee types");
      setEmployeeCounts({
        permanent: 0,
        dummy: 0,
        intern: 0,
        partial: 0,
        total: 0
      });
      setEmployeeTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ UPDATED: Fetch Salary Data from specific APIs
  const fetchSalaryData = useCallback(async (orgId, companyCode = "") => {
    if (!orgId) return;
    
    try {
      // 1. Fetch Total Salary from /HrDashboard/get-total-salary
      const totalSalaryUrl = `${Base_URL}/HrDashboard/get-total-salary?organisationId=${orgId}`;
      console.log("Fetching total salary from:", totalSalaryUrl);
      
      const totalSalaryRes = await fetch(totalSalaryUrl);
      let totalSalary = 0;
      
      if (totalSalaryRes.ok) {
        const totalSalaryData = await totalSalaryRes.json();
        totalSalary = totalSalaryData.totalSalary || totalSalaryData.amount || 0;
        console.log("Total salary data:", totalSalaryData);
      } else {
        console.warn("Failed to fetch total salary, using fallback");
        totalSalary = 450000; // Fallback value
      }

      // 2. Fetch Total Paid from /HrDashboard/get-total-e1-sum
      const totalPaidUrl = `${Base_URL}/HrDashboard/get-total-e1-sum`;
      console.log("Fetching total paid from:", totalPaidUrl);
      
      const totalPaidRes = await fetch(totalPaidUrl);
      let totalPaid = 0;
      
      if (totalPaidRes.ok) {
        const totalPaidData = await totalPaidRes.json();
        totalPaid = totalPaidData.totalAmount|| totalPaidData.amount || 0;
        console.log("Total paid data:", totalPaidData);
      } else {
        console.warn("Failed to fetch total paid, using fallback");
        totalPaid = 380000; // Fallback value
      }

      // 3. Total Pending = 0 (as requested)
      const totalPending = 0;

      setSalaryData({
        totalSalary,
        totalPaid,
        totalPending
      });
      
      console.log("Final salary data:", {
        totalSalary,
        totalPaid,
        totalPending
      });
      
    } catch (err) {
      console.error("Error fetching salary data:", err);
      // Fallback to dummy data if API calls fail
      setSalaryData({
        totalSalary: 450000,
        totalPaid: 380000,
        totalPending: 0 // Set to 0 as requested
      });
    }
  }, []);

  // ✅ NEW: Fetch REAL Attendance Data from your API
  const fetchRealAttendanceData = useCallback(async (orgId) => {
    if (!orgId) return;

    try {
      const url = `${Base_URL}HrDashboard/total-employees-multiple?/id?id=${orgId}`;
      console.log("Fetching real attendance data from:", url);

      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {  
          console.log("No attendance data found");
          setRealAttendanceData([]);
          return;
        }
        throw new Error(`Failed to fetch attendance data: ${response.status} ${response.statusText}`);
      }  
      const data = await response.json();
      const attendanceArray = Array.isArray(data) ? data : [];
      
      console.log("Raw attendance data:", attendanceArray);
      setRealAttendanceData(attendanceArray);
      
      // Process the real data to update attendance stats
      processRealAttendanceData(attendanceArray);
      
    } catch (err) {
      console.error("Error fetching real attendance data:", err);
      setRealAttendanceData([]);
      // Fallback to dummy data if real API fails
      setAttendanceData({
        present: 18,
        wfh: 6,
        leave: 4,
        total: 28
      });
    }
  }, []);

  // ✅ NEW: Process Real Attendance Data to Calculate Present, WFH, Leave counts
  const processRealAttendanceData = (attendanceArray) => {
    if (!attendanceArray || attendanceArray.length === 0) {
      setAttendanceData({
        present: 0,
        wfh: 0,
        leave: 0,
        total: 0
      });
      return;
    }

    let presentCount = 0;
    let wfhCount = 0;
    let leaveCount = 0;

    const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format

    attendanceArray.forEach(record => {
      // Check if the record is for today
      const recordDate = record.punchInDate ? new Date(record.punchInDate).toISOString().split('T')[0] : null;
      
      if (recordDate === today) {
        // Check if employee punched in (has punchInTime and punchInStatus is true)
        const hasPunchedIn = record.punchInTime && record.punchInTime !== "N/A" && record.punchInStatus === true;
        
        if (hasPunchedIn) {
          // Check if it's WFH (you might need to adjust this logic based on your data structure)
          const isWFH = record.address && record.address.toLowerCase().includes('home') || 
                       record.workMode === 'WFH' || 
                       (record.latitude === "N/A" && record.longitude === "N/A"); // Example logic
          
          if (isWFH) {
            wfhCount++;
          } else {
            presentCount++;
          }
        } else {
          // If no punch in, consider as leave
          leaveCount++;
        }
      } else {
        // If record is not for today, consider as leave (or adjust based on your business logic)
        leaveCount++;
      }
    });

    // Alternative logic: Count based on status if available
    // If your API provides direct status information, use this instead:
    const statusBasedCounts = countByStatus(attendanceArray, today);
    
    // Use status-based counts if available, otherwise use punch-in based counts
    if (statusBasedCounts.total > 0) {
      setAttendanceData(statusBasedCounts);
    } else {
      setAttendanceData({
        present: presentCount,
        wfh: wfhCount,
        leave: leaveCount,
        total: presentCount + wfhCount + leaveCount
      });
    }

    console.log("Processed attendance data:", {
      present: presentCount,
      wfh: wfhCount,
      leave: leaveCount,
      total: presentCount + wfhCount + leaveCount
    });
  };

  // ✅ NEW: Alternative: Count by status if your API provides status information
  const countByStatus = (attendanceArray, today) => {
    let present = 0;
    let wfh = 0;
    let leave = 0;
    let total = 0;

    attendanceArray.forEach(record => {
      const recordDate = record.punchInDate ? new Date(record.punchInDate).toISOString().split('T')[0] : null;
      
      if (recordDate === today) {
        // Check if status field exists in your data
        if (record.status) {
          const status = record.status.toLowerCase();
          if (status.includes('present') || status.includes('active')) {
            if (status.includes('wfh') || record.workMode === 'WFH') {
              wfh++;
            } else {
              present++;
            }
          } else if (status.includes('leave') || status.includes('absent')) {
            leave++;
          }
        } 
        // If no status field, use punch-in logic
        else if (record.punchInStatus && record.punchInTime && record.punchInTime !== "N/A") {
          if (record.workMode === 'WFH' || (record.address && record.address.toLowerCase().includes('home'))) {
            wfh++;
          } else {
            present++;
          }
        } else {
          leave++;
        }
        total++;
      }
    });

    return { present, wfh, leave, total };
  };

  // ✅ UPDATED: Fetch Attendance Data - Now uses real API
  const fetchAttendanceData = useCallback(async (orgId, companyCode = "") => {
    if (orgId) {
      // Use the new function that fetches from your real API
      await fetchRealAttendanceData(orgId);
    } else {
      // Fallback to dummy data if no orgId
      setAttendanceData({
        present: 18,
        wfh: 6,
        leave: 4,
        total: 28
      });
    }
  }, [fetchRealAttendanceData]);

  // ✅ Fetch Planned Leave Data
  const fetchPlannedLeaveData = useCallback(async (orgId, companyCode = "") => {
    try {
      const params = new URLSearchParams();
      params.append('orgId', orgId);
      if (companyCode && companyCode !== "") {
        params.append('companyCode', companyCode);
      }

      const res = await fetch(`${Base_URL}/HrDashboard/planned-leaves?${params.toString()}`);
      
      if (res.ok) {
        const data = await res.json();
        setPlannedLeaveData(Array.isArray(data) ? data : []);
      } else {
        setPlannedLeaveData([
          { date: '2024-01-15', employeeCount: 2 },
          { date: '2024-01-16', employeeCount: 1 },
          { date: '2024-01-18', employeeCount: 4 },
          { date: '2024-01-20', employeeCount: 3 },
          { date: '2024-01-22', employeeCount: 1 },
          { date: '2024-01-25', employeeCount: 2 },
          { date: '2024-01-26', employeeCount: 3 },
          { date: '2024-01-28', employeeCount: 1 }
        ]);
      }
    } catch (err) {
      console.error("Error fetching planned leave data:", err);
      setPlannedLeaveData([
        { date: '2024-01-15', employeeCount: 2 },
        { date: '2024-01-16', employeeCount: 1 },
        { date: '2024-01-18', employeeCount: 4 },
        { date: '2024-01-20', employeeCount: 3 },
        { date: '2024-01-22', employeeCount: 1 },
        { date: '2024-01-25', employeeCount: 2 },
        { date: '2024-01-26', employeeCount: 3 },
        { date: '2024-01-28', employeeCount: 1 }
      ]);
    }
  }, []);

  // ✅ Fetch Employee Trend Data
  const fetchEmployeeTrendData = useCallback(async (orgId, companyCode = "") => {
    try {
      const params = new URLSearchParams();
      params.append('orgId', orgId);
      if (companyCode && companyCode !== "") {
        params.append('companyCode', companyCode);
      }

      const res = await fetch(`${Base_URL}/HrDashboard/employee-trend?${params.toString()}`);
      
      if (res.ok) {
        const data = await res.json();
        setEmployeeTrendData(Array.isArray(data) ? data : []);
      } else {
        setEmployeeTrendData([
          { month: 'Jan', count: 25 },
          { month: 'Feb', count: 28 },
          { month: 'Mar', count: 30 },
          { month: 'Apr', count: 32 },
          { month: 'May', count: 35 },
          { month: 'Jun', count: 38 }
        ]);
      }
    } catch (err) {
      console.error("Error fetching employee trend data:", err);
      setEmployeeTrendData([
        { month: 'Jan', count: 25 },
        { month: 'Feb', count: 28 },
        { month: 'Mar', count: 30 },
        { month: 'Apr', count: 32 },
        { month: 'May', count: 35 },
        { month: 'Jun', count: 38 }
      ]);
    }
  }, []);

  // ✅ Fetch Hiring Exit Data
  const fetchHiringExitData = useCallback(async (orgId, companyCode = "") => {
    try {
      const params = new URLSearchParams();
      params.append('orgId', orgId);
      if (companyCode && companyCode !== "") {
        params.append('companyCode', companyCode);
      }

      const res = await fetch(`${Base_URL}/HrDashboard/hiring-exit?${params.toString()}`);
      
      if (res.ok) {
        const data = await res.json();
        setHiringExitData(Array.isArray(data) ? data : []);
      } else {
        setHiringExitData([
          { month: 'Jan', hired: 5, exited: 2 },
          { month: 'Feb', hired: 3, exited: 1 },
          { month: 'Mar', hired: 4, exited: 2 },
          { month: 'Apr', hired: 6, exited: 1 },
          { month: 'May', hired: 3, exited: 3 },
          { month: 'Jun', hired: 5, exited: 2 }
        ]);
      }
    } catch (err) {
      console.error("Error fetching hiring exit data:", err);
      setHiringExitData([
        { month: 'Jan', hired: 5, exited: 2 },
        { month: 'Feb', hired: 3, exited: 1 },
        { month: 'Mar', hired: 4, exited: 2 },
        { month: 'Apr', hired: 6, exited: 1 },
        { month: 'May', hired: 3, exited: 3 },
        { month: 'Jun', hired: 5, exited: 2 }
      ]);
    }
  }, []);

  // ✅ Categorize Employee
  const categorizeEmployee = (employee) => {
    const description = (employee.Description || employee.description || '').toLowerCase();
    const employeeType = (employee.EmployeeType || employee.employeeType || employee.type || '').toString().toLowerCase();
    
    if (description.includes('permanent') || employeeType.includes('permanent') || employeeType === '1') return 'permanent';
    if (description.includes('dummy') || employeeType.includes('dummy') || employeeType === '2') return 'dummy';
    if (description.includes('intern') || employeeType.includes('intern') || employeeType === '3') return 'intern';
    if (description.includes('partial') || employeeType.includes('partial') || employeeType === '4') return 'partial';
    
    switch(employeeType) {
      case '1': case 'permanent': return 'permanent';
      case '2': case 'dummy': return 'dummy';
      case '3': case 'intern': return 'intern';
      case '4': case 'partial': return 'partial';
      default: return null;
    }
  };

  // ✅ Process Employee Counts
  const processEmployeeCounts = (data) => {
    let allEmployees = [];
    
    if (Array.isArray(data)) {
      allEmployees = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.employees) || Array.isArray(data.data)) {
        allEmployees = data.employees || data.data;
      } else {
        allEmployees = [data];
      }
    }
    
    const counts = {
      permanent: 0,
      dummy: 0,
      intern: 0,
      partial: 0,
      total: 0
    };

    allEmployees.forEach((emp) => {
      const total = parseInt(emp.Total_Employees || emp.total_Employees || emp.totalEmployees || emp.count || emp.total || 0);
      if (total === 0) return;
      
      const category = categorizeEmployee(emp);
      
      if (category && counts.hasOwnProperty(category)) {
        counts[category] += total;
      }
    });

    counts.total = counts.permanent + counts.dummy + counts.intern + counts.partial;
    setEmployeeCounts(counts);
    setEmployeeTypes(allEmployees);
  };

  // ✅ NEW: Get detailed breakdown by employee type from real data
  const getEmployeeTypeBreakdown = () => {
    if (!realAttendanceData || realAttendanceData.length === 0) {
      return {
        permanent: 0,
        dummy: 0,
        intern: 0,
        partial: 0
      };
    }

    const today = new Date().toISOString().split('T')[0];
    const breakdown = {
      permanent: 0,
      dummy: 0,
      intern: 0,
      partial: 0
    };

    realAttendanceData.forEach(record => {
      const recordDate = record.punchInDate ? new Date(record.punchInDate).toISOString().split('T')[0] : null;
      
      if (recordDate === today && record.punchInStatus && record.punchInTime && record.punchInTime !== "N/A") {
        // Determine employee type from record
        const employeeType = getEmployeeTypeFromRecord(record);
        
        if (breakdown.hasOwnProperty(employeeType)) {
          breakdown[employeeType]++;
        }
      }
    });

    return breakdown;
  };

  // ✅ NEW: Helper function to determine employee type from record
  const getEmployeeTypeFromRecord = (record) => {
    // Adjust this logic based on how employee type is stored in your attendance data
    const role = (record.role || '').toLowerCase();
    const employeeType = (record.employeeType || record.type || '').toString().toLowerCase();
    
    if (role.includes('permanent') || employeeType.includes('permanent') || employeeType === '1') return 'permanent';
    if (role.includes('dummy') || employeeType.includes('dummy') || employeeType === '2') return 'dummy';
    if (role.includes('intern') || employeeType.includes('intern') || employeeType === '3') return 'intern';
    if (role.includes('partial') || employeeType.includes('partial') || employeeType === '4') return 'partial';
    
    return 'permanent'; // default fallback
  };

  const handlePresentBarClick = () => {
    if (filters.orgId) {
      navigate('/attendance-details', {
        state: {
          filters: filters,
          attendanceType: 'present',
          user: user 
        }
      });
    } else {
      setError("Please select an organization first");
    }
  };

  const handleOrgChange = (e) => {
    const selectedOrgId = e.target.value;
    const selectedOrg = orgIds.find(org => org.id == selectedOrgId);

    setFilters({
      orgId: user?.organisationId,
      companyCode: "",
      fiscalYear: "All Fiscal Years",
      postingDate: new Date().toISOString().split('T')[0]
    });

    setFilterDisplay({
      orgName: selectedOrg ? selectedOrg.name : "",
      companyName: ""
    });

    if (selectedOrgId) {
      fetchCompanyCodes(selectedOrgId);
      // Reset other data when organization changes
      setEmployeeCounts({
        permanent: 0,
        dummy: 0,
        intern: 0,
        partial: 0,
        total: 0
      });
      setEmployeeTypes([]);
    } else {
      setCompanyCodes([]);
    }
  };

  // ✅ Handle Company Selection
  const handleCompanySelect = (company) => {
    setFilters(prev => ({ 
      ...prev, 
      companyCode: company.code
    }));

    setFilterDisplay(prev => ({
      ...prev,
      companyName: company.name
    }));

    setCompanySearch(`${company.code} - ${company.name}`);
    setShowCompanyDropdown(false);
  };


  const handleFiscalYearChange = (e) => {
    const fiscalYear = e.target.value;
    setFilters(prev => ({ ...prev, fiscalYear }));
  };


  const handlePostingDateChange = (e) => {
    setFilters(prev => ({ ...prev, postingDate: e.target.value }));
  };

  const handleCompanySearchChange = (e) => {
    setCompanySearch(e.target.value);
    setShowCompanyDropdown(true);
  };

  
  const handleApplyFilters = () => {
    if (filters.orgId) {
      fetchEmployeeTypes(filters.orgId, filters.companyCode, filters.fiscalYear);
      fetchSalaryData(filters.orgId, filters.companyCode);
      fetchAttendanceData(filters.orgId, filters.companyCode); // This now uses real API
      fetchPlannedLeaveData(filters.orgId, filters.companyCode);
      fetchEmployeeTrendData(filters.orgId, filters.companyCode);
      fetchHiringExitData(filters.orgId, filters.companyCode);
    } else {
      setError("Please select an organization first");
    }
  };
  useEffect(() => {
    if (filters.orgId) {
      handleApplyFilters();
    }
  }, [filters.orgId, filters.companyCode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".hr-company-search-container")) {
        setShowCompanyDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchOrgIds();
    fetchFiscalYears();
  }, [fetchOrgIds, fetchFiscalYears]);

  // Filtered company codes for dropdown
  const filteredCompanyCodes = companyCodes.filter(company =>
    (company.name || "").toLowerCase().includes(companySearch.toLowerCase()) ||
    (company.code || "").toString().toLowerCase().includes(companySearch.toLowerCase())
  );

  // Format currency functions
  const formatCurrencyCompact = (amount) => {
    if (amount >= 10000000) {
      return '₹' + (amount / 10000000).toFixed(1) + 'Cr';
    } else if (amount >= 100000) {
      return '₹' + (amount / 100000).toFixed(1) + 'L';
    } else if (amount >= 1000) {
      return '₹' + (amount / 1000).toFixed(1) + 'K';
    }
    return '₹' + amount;
  };

  const formatCurrencyFull = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get active categories
  const getActiveCategories = () => {
    const categories = [];
    
    if (employeeCounts.total > 0) {
      categories.push({
        type: 'total',
        title: 'TOTAL EMPLOYEES',
        count: employeeCounts.total,
        icon: '📊'
      });
    }
    
    if (employeeCounts.permanent > 0) {
      categories.push({
        type: 'permanent',
        title: 'PERMANENT',
        count: employeeCounts.permanent,
        icon: '👨‍💼'
      });
    }
    
    if (employeeCounts.dummy > 0) {
      categories.push({
        type: 'dummy',
        title: 'DUMMY',
        count: employeeCounts.dummy,
        icon: '👥'
      });
    }
    
    if (employeeCounts.intern > 0) {
      categories.push({
        type: 'intern',
        title: 'INTERN',
        count: employeeCounts.intern,
        icon: '🎓'
      });
    }
    
    if (employeeCounts.partial > 0) {
      categories.push({
        type: 'partial',
        title: 'PARTIAL',
        count: employeeCounts.partial,
        icon: '⏱️'
      });
    }
    
    return categories;
  };

  // HR Count Card Component
  const HrCountCard = ({ title, count, type, totalEmployees, icon, amount, trend }) => (
    <div className={`hr-count-card hr-${type} ${trend || ''}`}>
      <div className="hr-card-bg-icon">{icon}</div>
      <div className="hr-card-content">
        <div className="hr-card-header">
          <div className="hr-card-icon">{icon}</div>
          <div className="hr-card-trend">
            {trend === 'up' && '↗️'}
            {trend === 'down' && '↘️'}
            {trend === 'stable' && '➡️'}
          </div>
        </div>
        <div className="hr-card-title">{title}</div>
        {amount ? (
          <>
            <div className="hr-card-amount">{formatCurrencyCompact(count)}</div>
            <div className="hr-card-full-amount">{formatCurrencyFull(count)}</div>
          </>
        ) : (
          <div className="hr-card-count">{count}</div>
        )}
        {type !== 'total' && totalEmployees > 0 && !amount && (
          <div className="hr-card-percentage">
            {Math.round((count / totalEmployees) * 100)}%
          </div>
        )}
        {amount && (
          <div className="hr-card-subtitle">
            {type === 'totalSalary' && 'Total Monthly'}
            {type === 'totalPaid' && 'Amount Disbursed'}
            {type === 'totalPending' && 'Pending Clearance'}
          </div>
        )}
      </div>
      <div className="hr-card-glow"></div>
    </div>
  );

  // ✅ UPDATED: Salary Cards Component with API data
 const SalaryCards = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlySalaryData, setMonthlySalaryData] = useState({
    totalSalary: 0,
    totalPaid: 0,
    totalPending: 0
  });
  const [loadingMonthly, setLoadingMonthly] = useState(false);

  // Generate months array
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Generate years array (last 5 years + current year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Fetch Monthly Salary Data
const fetchMonthlySalaryData = useCallback(async (orgId, month, year) => {
  if (!orgId) return;
    
  setLoadingMonthly(true);

  try {
    // 1️⃣ Fetch TOTAL SALARY
    const totalSalaryUrl = `${Base_URL}/HrDashboard/get-total-salary?organisationId=${orgId}&month=${month}&year=${year}`;
    const totalSalaryRes = await fetch(totalSalaryUrl);

    let totalSalary = 0;

    if (totalSalaryRes.ok) {
      const totalSalaryData = await totalSalaryRes.json();
      totalSalary = totalSalaryData.totalSalary || totalSalaryData.amount || 0;
    }

    // 2️⃣ Fetch TOTAL PAID with org_id
    const totalPaidUrl = `${Base_URL}/HrDashboard/get-year-and-month-sum/${month}?org_id=${orgId}`;
    const totalPaidRes = await fetch(totalPaidUrl);

    let totalPaid = 0;

    if (totalPaidRes.ok) {
      const totalPaidData = await totalPaidRes.json();
      totalPaid = totalPaidData[0]?.totalAmount || 0;
      console.log("Total Paid:", totalPaidData);
    }

    // 3️⃣ Pending
    const totalPending = Math.max(0, totalSalary - totalPaid);

    setMonthlySalaryData({
      totalSalary,
      totalPaid,
      totalPending
    });

  } catch (err) {
    console.error("Error fetching monthly salary data:", err);
  } finally {
    setLoadingMonthly(false);
  }

}, []);



  // Handle month change
  const handleMonthChange = (e) => {
    const month = parseInt(e.target.value);
    setSelectedMonth(month);
    if (filters.orgId) {
      fetchMonthlySalaryData(filters.orgId, month, selectedYear);
    }
  };

  // Handle year change
  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    if (filters.orgId) {
      fetchMonthlySalaryData(filters.orgId, selectedMonth, year);
    }
  };

  // Fetch monthly data when component mounts or org changes
  useEffect(() => {
    if (filters.orgId) {
      fetchMonthlySalaryData(filters.orgId, selectedMonth, selectedYear);
    }
  }, [filters.orgId]);

  return (
    <div className="hr-salary-section">
      <div className="hr-section-header">
        <div className="hr-section-title-container">
          <h3 className="hr-section-title">💰 Salary Overview</h3>
          <div className="hr-month-year-selector">
            <select 
              value={selectedMonth} 
              onChange={handleMonthChange}
              className="hr-month-select"
              disabled={loadingMonthly}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            <select 
              value={selectedYear} 
              onChange={handleYearChange}
              className="hr-year-select"
              disabled={loadingMonthly}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            {loadingMonthly && <div className="hr-loading-small">⏳</div>}
          </div>
        </div>
        <button 
          className="hr-view-all-btn"
          onClick={() => navigate('/payroll-dashboard', {
            state: {
              defaultTab: 'overview',
              filters: filters,
              selectedMonth: selectedMonth,
              selectedYear: selectedYear
            }
          })}
        >
          View Payroll Details →
        </button>
      </div>

      <div className="hr-salary-grid">
        {/* Total Salary Card */}
        <div 
          className={`hr-count-card hr-totalSalary ${monthlySalaryData.totalSalary > 0 ? 'clickable' : ''}`}
          onClick={() => monthlySalaryData.totalSalary > 0 && navigate('/payroll-dashboard', { 
            state: { 
              defaultTab: 'overview',
              filters: filters,
              selectedMonth: selectedMonth,
              selectedYear: selectedYear
            }
          })}
          style={{ cursor: monthlySalaryData.totalSalary > 0 ? 'pointer' : 'default' }}
        >
          <div className="hr-card-bg-icon">💸</div>
          <div className="hr-card-content">
            <div className="hr-card-header">
              <div className="hr-card-icon">💸</div>
              <div className="hr-card-trend">↗️</div>
            </div>
            <div className="hr-card-title">TOTAL SALARY</div>
            <div className="hr-card-amount">{formatCurrencyCompact(monthlySalaryData.totalSalary)}</div>
            <div className="hr-card-full-amount">{formatCurrencyFull(monthlySalaryData.totalSalary)}</div>
            <div className="hr-card-subtitle">
              {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </div>
            {monthlySalaryData.totalSalary > 0 && (
              <div className="hr-click-hint">Click to view payroll →</div>
            )}
          </div>
          <div className="hr-card-glow"></div>
        </div>

        {/* Total Paid Card - Data from /HrDashboard/get-year-and-month-sum/orgId */}
        <div 
          className={`hr-count-card hr-totalPaid ${monthlySalaryData.totalPaid > 0 ? 'clickable' : ''}`}
          onClick={() => monthlySalaryData.totalPaid > 0 && navigate('/payroll-dashboard', { 
            state: { 
              defaultTab: 'paid',
              filters: filters,
              selectedMonth: selectedMonth,
              selectedYear: selectedYear
            }
          })}
          style={{ cursor: monthlySalaryData.totalPaid > 0 ? 'pointer' : 'default' }}
        >
          <div className="hr-card-bg-icon">✅</div>
          <div className="hr-card-content">
            <div className="hr-card-header">
              <div className="hr-card-icon">✅</div>
              <div className="hr-card-trend">➡️</div>
            </div>
            <div className="hr-card-title">TOTAL PAID</div>
            <div className="hr-card-amount">{formatCurrencyCompact(monthlySalaryData.totalPaid)}</div>
            <div className="hr-card-full-amount">{formatCurrencyFull(monthlySalaryData.totalPaid)}</div>
            <div className="hr-card-subtitle">Amount Disbursed</div>
            {monthlySalaryData.totalPaid > 0 && (
              <div className="hr-click-hint">Click to view details →</div>
            )}
          </div>
          <div className="hr-card-glow"></div>
        </div>

        {/* Total Pending Card */}
        <div 
          className={`hr-count-card hr-totalPending ${monthlySalaryData.totalPending > 0 ? 'clickable' : ''}`}
          onClick={() => monthlySalaryData.totalPending > 0 && navigate('/payroll-dashboard', { 
            state: { 
              defaultTab: 'pending',
              filters: filters,
              selectedMonth: selectedMonth,
              selectedYear: selectedYear
            }
          })}
          style={{ cursor: monthlySalaryData.totalPending > 0 ? 'pointer' : 'default' }}
        >
          <div className="hr-card-bg-icon">⏳</div>
          <div className="hr-card-content">
            <div className="hr-card-header">
              <div className="hr-card-icon">⏳</div>
              <div className="hr-card-trend">➡️</div>
            </div>
            <div className="hr-card-title">TOTAL PENDING</div>
            <div className="hr-card-amount">{formatCurrencyCompact(monthlySalaryData.totalPending)}</div>
            <div className="hr-card-full-amount">{formatCurrencyFull(monthlySalaryData.totalPending)}</div>
            <div className="hr-card-subtitle">Pending Clearance</div>
            {monthlySalaryData.totalPending === 0 ? (
              <div className="hr-no-pending">No pending payments</div>
            ) : (
              <div className="hr-click-hint">Click to view pending →</div>
            )}
          </div>
          <div className="hr-card-glow"></div>
        </div>
      </div>

      {/* API Test Button */}
      
    </div>
  );
};

  // UPDATED: Attendance Chart Component with Real Data
  const AttendanceChart = () => {
    const totalPresent = attendanceData.present + attendanceData.wfh;
    const maxCount = Math.max(totalPresent, attendanceData.leave, 1);
    
    // Use real data breakdown instead of calculated percentages
    const employeeBreakdown = getEmployeeTypeBreakdown();
    const totalFromBreakdown = employeeBreakdown.permanent + employeeBreakdown.dummy + employeeBreakdown.intern + employeeBreakdown.partial;
    
    // Use actual counts from real data, fallback to calculated if no real data
    const permanentPresent = totalFromBreakdown > 0 ? employeeBreakdown.permanent : Math.round((employeeCounts.permanent / employeeCounts.total) * totalPresent);
    const dummyPresent = totalFromBreakdown > 0 ? employeeBreakdown.dummy : Math.round((employeeCounts.dummy / employeeCounts.total) * totalPresent);
    const internPresent = totalFromBreakdown > 0 ? employeeBreakdown.intern : Math.round((employeeCounts.intern / employeeCounts.total) * totalPresent);
    const partialPresent = totalFromBreakdown > 0 ? employeeBreakdown.partial : Math.round((employeeCounts.partial / employeeCounts.total) * totalPresent);

    return (
      chartVisibility.attendanceChart && (
        <div className="hr-chart-container hr-attendance-chart">
          <div className="hr-chart-header">
            <h3 className="hr-section-title">Today's Availability</h3>
            <div className="hr-chart-header-right">
              <div className="hr-chart-total">Total: {attendanceData.total}</div>
              <div className="hr-data-source-badge" title="Using real attendance data">
                📊 Live Data
              </div>
              <button 
                className="hr-hide-btn"
                onClick={() => toggleChartDataVisibility('attendanceChart')}
                title="Toggle chart data"
              >
                {chartDataVisibility.attendanceChart ? <EyeOff size={16} /> : <Eye size={16} />}
                {chartDataVisibility.attendanceChart ? "Hide Data" : "Show Data"}
              </button>
            </div>
          </div>
          {chartDataVisibility.attendanceChart ? (
            <>
              <div className="hr-attendance-bars-stacked">
                {/* Present Bar - Clickable */}
                <div className="hr-bar-group-stacked">
                  <div className="hr-bar-label">Present</div>
                  <div 
                    className="hr-bar-container-stacked clickable-bar"
                    onClick={handlePresentBarClick}
                    title="Click to view detailed attendance"
                  >
                    <div 
                      className="hr-bar-stacked hr-permanent-stacked" 
                      style={{ height: `${(totalPresent / maxCount) * 100}%` }}
                    >
                      <div className="hr-stacked-segment permanent" style={{ height: `${(permanentPresent / totalPresent) * 100}%` }}>
                        <span className="hr-stacked-label">P: {permanentPresent}</span>
                      </div>
                      <div className="hr-stacked-segment dummy" style={{ height: `${(dummyPresent / totalPresent) * 100}%` }}>
                        <span className="hr-stacked-label">D: {dummyPresent}</span>
                      </div>
                      <div className="hr-stacked-segment intern" style={{ height: `${(internPresent / totalPresent) * 100}%` }}>
                        <span className="hr-stacked-label">I: {internPresent}</span>
                      </div>
                      <div className="hr-stacked-segment partial" style={{ height: `${(partialPresent / totalPresent) * 100}%` }}>
                        <span className="hr-stacked-label">PT: {partialPresent}</span>
                      </div>
                    </div>
                    <span className="hr-bar-total">{totalPresent}</span>
                  </div>
                </div>
                
                {/* WFH Bar */}
                <div className="hr-bar-group-stacked">
                  <div className="hr-bar-label">WFH</div>
                  <div className="hr-bar-container-stacked">
                    <div 
                      className="hr-bar-stacked hr-wfh-stacked" 
                      style={{ height: `${(attendanceData.wfh / maxCount) * 100}%` }}
                    >
                      <span className="hr-bar-value">{attendanceData.wfh}</span>
                    </div>
                  </div>
                </div>
                
                {/* Leave Bar */}
                <div className="hr-bar-group-stacked">
                  <div className="hr-bar-label">Leave</div>
                  <div className="hr-bar-container-stacked">
                    <div 
                      className="hr-bar-stacked hr-leave-stacked" 
                      style={{ height: `${(attendanceData.leave / maxCount) * 100}%` }}
                    >
                      <span className="hr-bar-value">{attendanceData.leave}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hr-chart-axes">
                <div className="hr-y-axis">
                  <span>Number of Employees</span>
                </div>
                <div className="hr-x-axis">
                  <span>Attendance Status</span>
                </div>
              </div>
              <div className="hr-stacked-legend">
                <div className="hr-legend-item">
                  <div className="hr-legend-color permanent"></div>
                  <span>Permanent</span>
                </div>
                <div className="hr-legend-item">
                  <div className="hr-legend-color dummy"></div>
                  <span>Contract</span>
                </div>
                <div className="hr-legend-item">
                  <div className="hr-legend-color intern"></div>
                  <span>Intern</span>
                </div>
                <div className="hr-legend-item">
                  <div className="hr-legend-color partial"></div>
                  <span>Part-time</span>
                </div>
              </div>
              <div className="hr-chart-note">
                💡 Using real attendance data from API | Click on "Present" bar for details
              </div>
            </>
          ) : (
            <div className="no-chart-data">
              <Users size={48} color="#6b7280"/>
              <p>Chart data is hidden</p>
              <p className="data-hint">Click "Show Data" to display the chart</p>
            </div>
          )}
        </div>
      )
    );
  };

const MonthlyBirthdayList = ({ companyCode, isVisible = true }) => {
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showBalloons, setShowBalloons] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [todayBirthdays, setTodayBirthdays] = useState([]); 
  const currentMonth = new Date().getMonth() + 1;
  const currentDay = new Date().getDate();
  const orgId = user?.organisationId;
   const companycode = companyCodes;  
    console.log(companycode);                      

  const fetchBirthdays = useCallback(async () => {
    if (!orgId || !companyCode) return;

    setLoading(true);
    setError("");
    
    try {
      const url = `${Base_URL}/HrDashboard/brithday-month/${orgId}/${companyCode}/${currentMonth}`;
      console.log("Fetching birthdays from:", url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          setBirthdays([]);
          return;
        }
        throw new Error(`Failed to fetch birthdays: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Birthday API response:", data);
      
      // Check if data is array and has content
      if (!Array.isArray(data)) {
        console.error("Birthday API returned non-array data:", data);
        setBirthdays([]);
        return;
      }
      
      // Filter and format the data
      const formattedBirthdays = data
        .filter(employee => employee && employee.id) // Note: lowercase 'id' not 'Id'
  .map(employee => ({
    id: employee.id, // lowercase id
    fullName: employee.fullName, // lowercase fullName
    dob: employee.dob ? new Date(employee.dob) : null, // lowercase dob
    birthDay: employee.birthDay, // lowercase birthDay
    birthMonth: employee.birthMonth, // lowercase birthMonth
    jobTitle: employee.offerRoleDesc || "Employee", // lowercase offerRoleDesc
    isToday: employee.birthDay === currentDay
        }));
      
      console.log("Formatted birthdays:", formattedBirthdays);
      setBirthdays(formattedBirthdays);
      
      // Check for today's birthdays
      const todayBdays = formattedBirthdays.filter(emp => emp.isToday);
      console.log("Today's birthdays:", todayBdays);
      
      if (todayBdays.length > 0) {
        setTodayBirthdays(todayBdays);
        setShowBanner(true);
        setShowBalloons(true);
        
        // Auto-hide balloons after animation
        setTimeout(() => {
          setShowBalloons(false);
        }, 10000); // Hide after 10 seconds
        
        // Auto-hide banner after 30 seconds
        setTimeout(() => {
          setShowBanner(false);
        }, 30000);
      }
      
    } catch (err) {
      console.error("Error fetching birthdays:", err);
      setError(err.message || "Failed to load birthday data");
    } finally {
      setLoading(false);
    }
  }, [orgId, companyCode, currentMonth]);

  useEffect(() => {
    if (orgId && companyCode) {
      fetchBirthdays();
    } else {
      setBirthdays([]);
      setShowBalloons(false);
      setShowBanner(false);
    }
  }, [orgId, companyCode, fetchBirthdays]);

  // Add a refresh button handler
  const handleRefresh = () => {
    if (orgId && companyCode) {
      fetchBirthdays();
    }
  };

  // Generate avatar initials
  const getAvatarInitials = (name) => {
    if (!name || typeof name !== 'string') return '??';
    const parts = name.trim().split(' ');
    if (parts.length === 0) return '??';
    
    return parts
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Format date for display
  const formatBirthdayDate = (day, month) => {
    try {
      const date = new Date(new Date().getFullYear(), month - 1, day);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return `Day ${day}, Month ${month}`;
    }
  };

  // Create balloons for animation
  const renderBalloons = () => {
    if (!showBalloons) return null;
    
    return (
      <div className="balloon-container">
        {[...Array(9)].map((_, i) => (
          <div key={i} className="balloon"></div>
        ))}
      </div>
    );
  };

  // Birthday Banner
  const renderBirthdayBanner = () => {
    if (!showBanner || todayBirthdays.length === 0) return null;
    
    const names = todayBirthdays.map(emp => emp.fullName);
    let message = '';
    
    if (names.length === 1) {
      message = `🎉 Happy Birthday! Today is ${names[0]}'s special day!`;
    } else if (names.length === 2) {
      message = `🎉 Happy Birthday! Today is ${names.join(' and ')}'s special day!`;
    } else {
      const last = names.pop();
      message = `🎉 Happy Birthday! Today is ${names.join(', ')}, and ${last}'s special day!`;
    }
    
    return (
      <div className="birthday-banner">
        <div className="banner-icon">🎂</div>
        <div className="banner-message">{message}</div>
        <button 
          className="banner-close-btn"
          onClick={() => setShowBanner(false)}
          aria-label="Close banner"
        >
          ×
        </button>
      </div>
    );
  };

  // If component should not be visible
  if (!isVisible) return null;

  // Check if user is authenticated
  if (!user) {
    return (
      <div className="birthday-container">
        <div className="birthday-header">
          <h3>🎂 Monthly Birthday List</h3>
        </div>
        <div className="no-birthdays">
          <p>Please login to view birthdays</p>
        </div>
      </div>
    );
  }

  // Check if we have valid orgId and companyCode
  const hasValidParams = orgId && companyCode;
  
  if (!hasValidParams) {
    return (
      <div className="birthday-container">
        <div className="birthday-header">
          <h3>🎂 Monthly Birthday List</h3>
          <button 
            onClick={handleRefresh}
            className="refresh-btn"
            disabled={!orgId || !companyCode}
            title="Refresh birthdays"
          >
            🔄
          </button>
        </div>
        <div className="no-birthdays">
          <p>Select a company to view birthdays</p>
          <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '8px' }}>
            Organization: {orgId || 'Not available'}, 
            Company: {companyCode || 'Not selected'}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="birthday-container">
        <div className="birthday-header">
          <h3>🎂 Monthly Birthday List ({new Date().toLocaleString('default', { month: 'long' })})</h3>
        </div>
        <div className="birthday-loading">
          <div className="birthday-spinner"></div>
          <p>Loading birthdays...</p>
          <p style={{ fontSize: '12px', color: '#a0aec0' }}>
            Fetching from Organization {orgId}, Company {companyCode}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="birthday-container">
        <div className="birthday-header">
          <h3>🎂 Monthly Birthday List</h3>
          <button 
            onClick={handleRefresh}
            className="refresh-btn"
            title="Retry fetching birthdays"
          >
            🔄
          </button>
        </div>
        <div className="no-birthdays error-state">
          <p style={{ color: '#ff6b6b' }}>Error: {error}</p>
          <button 
            onClick={handleRefresh}
            className="retry-btn"
          >
            Try Again
          </button>
          <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '8px' }}>
            URL: {Base_URL}/HrDashboard/brithday-month/{orgId}/{companyCode}/{currentMonth}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {renderBalloons()}
      {renderBirthdayBanner()}
      
      <div className="birthday-container">
        <div className="birthday-header">
          <h3>🎂 Monthly Birthday List ({new Date().toLocaleString('default', { month: 'long' })})</h3>
          <div className="birthday-header-right">
            <span className="birthday-count">({birthdays.length} birthdays)</span>
            <button 
              onClick={handleRefresh}
              className="refresh-btn"
              title="Refresh birthdays"
            >
              🔄
            </button>
          </div>
        </div>
        
        {birthdays.length === 0 ? (
          <div className="no-birthdays">
            <p>🎈 No birthdays this month</p>
            <p style={{ fontSize: '14px', color: '#a0aec0' }}>Spread some cheer anyway!</p>
            <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '8px' }}>
              Organization: {orgId}, Company: {companyCode}
            </p>
          </div>
        ) : (
          <div className="birthday-list">
            {birthdays.map((employee) => (
              <div 
                key={employee.id} 
                className={`birthday-item ${employee.isToday ? 'today' : ''}`}
              >
                {employee.isToday && (
                  <div className="today-badge">TODAY!</div>
                )}
                
                <div className="employee-avatar">
                  {getAvatarInitials(employee.fullName)}
                </div>
                
                <div className="employee-details">
                  <div className="employee-name">{employee.fullName}</div>
                  <div className="employee-title">{employee.jobTitle}</div>
                  <div className="birthday-date">
                    <span className="birthday-icon">🎂</span>
                    {formatBirthdayDate(employee.birthDay, employee.birthMonth)}
                    {employee.isToday && (
                      <span style={{ color: '#ff6b6b', fontWeight: 'bold', marginLeft: '8px' }}>
                        🎉 Celebrating Today!
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
  // Planned Leave Chart Component
  const PlannedLeaveChart = () => {
    const maxEmployees = Math.max(...plannedLeaveData.map(item => item.employeeCount), 1);
    const displayData = plannedLeaveData.slice(0, 5);

    return (
      chartVisibility.plannedLeaves && (
        <div className="hr-chart-container hr-leave-chart">
          <div className="hr-chart-header">
            <h3 className="hr-section-title">Planned Leaves</h3>
            <div className="hr-chart-header-right">
              <div className="hr-chart-subtitle">Next 5 days</div>
              <button 
                className="hr-hide-btn"
                onClick={() => toggleChartDataVisibility('plannedLeaves')}
                title="Toggle chart data"
              >
                {chartDataVisibility.plannedLeaves ? <EyeOff size={16} /> : <Eye size={16} />}
                {chartDataVisibility.plannedLeaves ? "Hide Data" : "Show Data"}
              </button>
            </div>
          </div>
          {chartDataVisibility.plannedLeaves ? (
            <>
              <div className="hr-leave-bars">
                {displayData.map((item, index) => (
                  <div key={index} className="hr-leave-item">
                    <div className="hr-date-info">
                      <div className="hr-date-day">{new Date(item.date).getDate()}</div>
                      <div className="hr-date-month">{new Date(item.date).toLocaleString('default', { month: 'short' })}</div>
                    </div>
                    <div className="hr-bar-wrapper">
                      <div 
                        className="hr-horizontal-bar" 
                        style={{ width: `${(item.employeeCount / maxEmployees) * 100}%` }}
                      >
                        <span className="hr-employee-count">{item.employeeCount}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {plannedLeaveData.length > 5 && (
                <div className="hr-more-items">
                  +{plannedLeaveData.length - 5} more days
                </div>
              )}
              <div className="hr-chart-axes">
                <div className="hr-x-axis">
                  <span>Employees on Leave</span>
                </div>
              </div>
            </>
          ) : (
            <div className="no-chart-data">
              <Calendar size={48} color="#6b7280"/>
              <p>Chart data is hidden</p>
              <p className="data-hint">Click "Show Data" to display the chart</p>
            </div>
          )}
        </div>
      )
    );
  };

  // Total Employee Box Component
  const TotalEmployeeBox = () => (
    <div className="hr-total-box">
      <div className="hr-total-header">
        <div className="hr-total-icon">👥</div>
        <div className="hr-total-info">
          <div className="hr-total-main">Total Workforce</div>
          <div className="hr-total-number">{employeeCounts.total}</div>
          <div className="hr-total-sub">Active Employees</div>
        </div>
      </div>
      <div className="hr-total-breakdown">
        <div className="hr-breakdown-item">
          <span className="hr-breakdown-label">Permanent</span>
          <span className="hr-breakdown-value">{employeeCounts.permanent}</span>
        </div>
        <div className="hr-breakdown-item">
          <span className="hr-breakdown-label">Contract</span>
          <span className="hr-breakdown-value">{employeeCounts.dummy}</span>
        </div>
        <div className="hr-breakdown-item">
          <span className="hr-breakdown-label">Interns</span>
          <span className="hr-breakdown-value">{employeeCounts.intern}</span>
        </div>
        <div className="hr-breakdown-item">
          <span className="hr-breakdown-label">Part-time</span>
          <span className="hr-breakdown-value">{employeeCounts.partial}</span>
        </div>
      </div>
      <div className="hr-total-metrics">
        <div className="hr-metric-item">
          <div className="hr-metric-value">+5.2%</div>
          <div className="hr-metric-label">Growth</div>
        </div>
        <div className="hr-metric-item">
          <div className="hr-metric-value">94.8%</div>
          <div className="hr-metric-label">Retention</div>
        </div>
        <div className="hr-metric-item">
          <div className="hr-metric-value">12.3%</div>
          <div className="hr-metric-label">Attrition</div>
        </div>
      </div>
    </div>
  );

  // Employee Trend Chart Component
  const EmployeeTrendChart = () => {
    const maxCount = Math.max(...employeeTrendData.map(item => item.count), 1);
    
    return (
      chartVisibility.employeeTrend && (
        <div className="hr-chart-container hr-trend-chart">
          <div className="hr-chart-header">
            <h3 className="hr-section-title">📈 Employee Growth Trend</h3>
            <div className="hr-chart-header-right">
              <div className="hr-chart-subtitle">Last 6 months</div>
              <button 
                className="hr-hide-btn"
                onClick={() => toggleChartDataVisibility('employeeTrend')}
                title="Toggle chart data"
              >
                {chartDataVisibility.employeeTrend ? <EyeOff size={16} /> : <Eye size={16} />}
                {chartDataVisibility.employeeTrend ? "Hide Data" : "Show Data"}
              </button>
            </div>
          </div>
          {chartDataVisibility.employeeTrend ? (
            <div className="hr-trend-chart-content">
              <div className="hr-line-chart">
                <div className="hr-chart-grid">
                  {employeeTrendData.map((item, index) => (
                    <div key={index} className="hr-data-point">
                      <div 
                        className="hr-point" 
                        style={{ bottom: `${(item.count / maxCount) * 100}%` }}
                        data-value={item.count}
                      ></div>
                      <div className="hr-month-label">{item.month}</div>
                    </div>
                  ))}
                </div>
                <svg className="hr-trend-line" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <path 
                    d={employeeTrendData.map((item, index) => {
                      const x = (index / (employeeTrendData.length - 1)) * 100;
                      const y = 100 - (item.count / maxCount) * 100;
                      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                    }).join(' ')}
                    fill="none"
                    stroke="#667eea"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <div className="hr-trend-values">
                {employeeTrendData.map((item, index) => (
                  <div key={index} className="hr-trend-value">
                    <span className="hr-trend-count">{item.count}</span>
                    <span className="hr-trend-month">{item.month}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-chart-data">
              <TrendingUp size={48} color="#6b7280"/>
              <p>Chart data is hidden</p>
              <p className="data-hint">Click "Show Data" to display the chart</p>
            </div>
          )}
        </div>
      )
    );
  };

  // Hiring Exit Chart Component
  const HiringExitChart = () => {
    const maxCount = Math.max(...hiringExitData.map(item => Math.max(item.hired, item.exited)), 1);
    
    return (
      chartVisibility.hiringExit && (
        <div className="hr-chart-container hr-hiring-chart">
          <div className="hr-chart-header">
            <h3 className="hr-section-title">👥 Hiring vs Exit</h3>
            <div className="hr-chart-header-right">
              <div className="hr-chart-subtitle">Monthly comparison</div>
              <button 
                className="hr-hide-btn"
                onClick={() => toggleChartDataVisibility('hiringExit')}
                title="Toggle chart data"
              >
                {chartDataVisibility.hiringExit ? <EyeOff size={16} /> : <Eye size={16} />}
                {chartDataVisibility.hiringExit ? "Hide Data" : "Show Data"}
              </button>
            </div>
          </div>
          {chartDataVisibility.hiringExit ? (
            <>
              <div className="hr-hiring-bars">
                {hiringExitData.map((item, index) => (
                  <div key={index} className="hr-hiring-group">
                    <div className="hr-hiring-label">{item.month}</div>
                    <div className="hr-hiring-bars-container">
                      <div 
                        className="hr-hiring-bar hired" 
                        style={{ height: `${(item.hired / maxCount) * 100}%` }}
                      >
                        <span className="hr-hiring-value">{item.hired}</span>
                      </div>
                      <div 
                        className="hr-hiring-bar exited" 
                        style={{ height: `${(item.exited / maxCount) * 100}%` }}
                      >
                        <span className="hr-hiring-value">{item.exited}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hr-chart-axes">
                <div className="hr-y-axis">
                  <span>Number of Employees</span>
                </div>
                <div className="hr-x-axis">
                  <span>Months</span>
                </div>
              </div>
              <div className="hr-hiring-legend">
                <div className="hr-legend-item">
                  <div className="hr-legend-color hired"></div>
                  <span>Hired</span>
                </div>
                <div className="hr-legend-item">
                  <div className="hr-legend-color exited"></div>
                  <span>Exited</span>
                </div>
              </div>
            </>
          ) : (
            <div className="no-chart-data">
              <Users size={48} color="#6b7280"/>
              <p>Chart data is hidden</p>
              <p className="data-hint">Click "Show Data" to display the chart</p>
            </div>
          )}
        </div>
      )
    );
  };

  // UPDATED: Present Employees Component with Real Data
  const PresentEmployees = () => {
    const totalPresent = attendanceData.present + attendanceData.wfh;
    
    return (
      chartVisibility.presentEmployees && (
        <div className="hr-present-employees">
          <div className="hr-present-header">
            <h3 className="hr-section-title">👥 Present Today</h3>
            <div className="hr-present-header-right">
              <div className="hr-present-total">{totalPresent} Employees</div>
              <div className="hr-data-source-badge mini" title="Using real attendance data">
                Live
              </div>
              <button 
                className="hr-hide-btn"
                onClick={() => toggleChartDataVisibility('presentEmployees')}
                title="Toggle chart data"
              >
                {chartDataVisibility.presentEmployees ? <EyeOff size={16} /> : <Eye size={16} />}
                {chartDataVisibility.presentEmployees ? "Hide Data" : "Show Data"}
              </button>
            </div>
          </div>
          {chartDataVisibility.presentEmployees ? (
            <>
              <div className="hr-present-stats">
                <div className="hr-present-stat">
                  <div className="hr-stat-icon">🏢</div>
                  <div className="hr-stat-content">
                    <div className="hr-stat-count">{attendanceData.present}</div>
                    <div className="hr-stat-label">In Office</div>
                  </div>
                </div>
                <div className="hr-present-stat">
                  <div className="hr-stat-icon">🏠</div>
                  <div className="hr-stat-content">
                    <div className="hr-stat-count">{attendanceData.wfh}</div>
                    <div className="hr-stat-label">Work From Home</div>
                  </div>
                </div>
              </div>
              {totalPresent > 0 && (
                <div className="hr-present-percentage">
                  <div className="hr-percentage-bar">
                    <div 
                      className="hr-percentage-fill office" 
                      style={{ width: `${((attendanceData.present) / totalPresent) * 100}%` }}
                    >
                      <span className="hr-percentage-text">
                        {Math.round((attendanceData.present / totalPresent) * 100)}% Office
                      </span>
                    </div>
                    <div 
                      className="hr-percentage-fill wfh" 
                      style={{ width: `${((attendanceData.wfh) / totalPresent) * 100}%` }}
                    >
                      <span className="hr-percentage-text">
                        {Math.round((attendanceData.wfh / totalPresent) * 100)}% WFH
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-chart-data">
              <Users size={48} color="#6b7280"/>
              <p>Chart data is hidden</p>
              <p className="data-hint">Click "Show Data" to display the chart</p>
            </div>
          )}
        </div>
      )
    );
  };

  // On Time Performance Component
  const OnTimePerformance = () => {
    const mockOnTimeData = [
      { label: 'On Time', count: 22, percentage: 88 },
      { label: 'Delayed', count: 3, percentage: 12 }
    ];

    return (
      chartVisibility.onTimePerformance && (
        <div className="hr-on-time-performance">
          <div className="hr-performance-header">
            <h3 className="hr-section-title">⏰ On-Time Performance</h3>
            <div className="hr-performance-header-right">
              <div className="hr-performance-total">Today</div>
              <button 
                className="hr-hide-btn"
                onClick={() => toggleChartDataVisibility('onTimePerformance')}
                title="Toggle chart data"
              >
                {chartDataVisibility.onTimePerformance ? <EyeOff size={16} /> : <Eye size={16} />}
                {chartDataVisibility.onTimePerformance ? "Hide Data" : "Show Data"}
              </button>
            </div>
          </div>
          {chartDataVisibility.onTimePerformance ? (
            <>
              <div className="hr-performance-bars">
                {mockOnTimeData.map((item, index) => (
                  <div key={index} className="hr-performance-bar-group">
                    <div className="hr-performance-label">{item.label}</div>
                    <div className="hr-performance-bar-container">
                      <div 
                        className={`hr-performance-bar ${item.label.toLowerCase().replace(' ', '-')}`}
                        style={{ width: `${item.percentage}%` }}
                      >
                        <span className="hr-performance-count">{item.count}</span>
                      </div>
                    </div>
                    <div className="hr-performance-percentage">{item.percentage}%</div>
                  </div>
                ))}
              </div>
              <div className="hr-performance-summary">
                <div className="hr-summary-item">
                  <span className="hr-summary-label">Total Check-ins</span>
                  <span className="hr-summary-value">{mockOnTimeData[0].count + mockOnTimeData[1].count}</span>
                </div>
                <div className="hr-summary-item">
                  <span className="hr-summary-label">On Time Rate</span>
                  <span className="hr-summary-value">{mockOnTimeData[0].percentage}%</span>
                </div>
              </div>
            </>
          ) : (
            <div className="no-chart-data">
              <Activity size={48} color="#6b7280"/>
              <p>Chart data is hidden</p>
              <p className="data-hint">Click "Show Data" to display the chart</p>
            </div>
          )}
        </div>
      )
    );
  };

  // Employee Delay Chart Component
  const EmployeeDelayChart = () => {
    const mockDelayData = [
      { name: 'Rajesh Kumar', delayDays: 5, department: 'Engineering' },
      { name: 'Priya Sharma', delayDays: 4, department: 'Marketing' },
      { name: 'Amit Patel', delayDays: 3, department: 'Sales' },
      { name: 'Sneha Reddy', delayDays: 3, department: 'HR' },
      { name: 'Vikram Singh', delayDays: 2, department: 'Operations' }
    ];

    const maxDelayDays = Math.max(...mockDelayData.map(item => item.delayDays), 1);

    return (
      chartVisibility.employeeDelay && (
        <div className="hr-delay-chart">
          <div className="hr-delay-header">
            <h3 className="hr-section-title">📊 Employee Delay Analysis</h3>
            <div className="hr-delay-header-right">
              <div className="hr-delay-subtitle">Last 30 Days</div>
              <button 
                className="hr-hide-btn"
                onClick={() => toggleChartDataVisibility('employeeDelay')}
                title="Toggle chart data"
              >
                {chartDataVisibility.employeeDelay ? <EyeOff size={16} /> : <Eye size={16} />}
                {chartDataVisibility.employeeDelay ? "Hide Data" : "Show Data"}
              </button>
            </div>
          </div>
          {chartDataVisibility.employeeDelay ? (
            <>
              <div className="hr-delay-bars">
                {mockDelayData.map((employee, index) => (
                  <div key={index} className="hr-delay-item">
                    <div className="hr-employee-info">
                      <div className="hr-employee-name">{employee.name}</div>
                      <div className="hr-employee-department">{employee.department}</div>
                    </div>
                    <div className="hr-delay-bar-container">
                      <div 
                        className="hr-delay-bar" 
                        style={{ width: `${(employee.delayDays / maxDelayDays) * 100}%` }}
                      >
                        <span className="hr-delay-days">{employee.delayDays} day{employee.delayDays !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hr-chart-axes">
                <div className="hr-y-axis">
                  <span>Employee Names</span>
                </div>
                <div className="hr-x-axis">
                  <span>Delay Days</span>
                </div>
              </div>
            </>
          ) : (
            <div className="no-chart-data">
              <Activity size={48} color="#6b7280"/>
              <p>Chart data is hidden</p>
              <p className="data-hint">Click "Show Data" to display the chart</p>
            </div>
          )}
        </div>
      )
    );
  };

  const activeCategories = getActiveCategories();

  return (
    <div className="hr-dashboard-container">
      <div className="hr-dashboard-header">
        <h2>HR Analytics Dashboard</h2>
        {error && <div className="hr-error-text">{error}</div>}
      </div>

      {/* ✅ Updated Filters Section (like Inventory dashboard) */}
      <div className="filters-container">
        <div className="filter-grid">
          {/* Organization ID */}
          <div className="filter-group">
            <label className="filter-label">Organization ID</label>
            <select
              value={filters.orgId}
              onChange={handleOrgChange}
              className="filter-select"
            >
              <option value="">-- Select Organization --</option>
              {orgIds.map((org, index) => (
                <option key={`org-${org.id}-${index}`} value={org.id}>
                  {org.id}-{org.name}
                </option>
              ))}
            </select>
          </div>

          {/* Company Code with Search */}
          <div className="filter-group">
            <label className="filter-label">Company Code</label>
            <div className="hr-company-search-container">
              <input
                type="text"
                placeholder="Search company code or name..."
                value={companySearch}
                onChange={handleCompanySearchChange}
                onFocus={() => {
                  if (filters.companyCode && companySearch === `${filters.companyCode} - ${filterDisplay.companyName}`) {
                    setCompanySearch("");
                  }
                  setShowCompanyDropdown(true);
                }}
                className="filter-input"
                autoComplete="off"
                disabled={!filters.orgId || companyCodes.length === 0}
              />

              {showCompanyDropdown && filteredCompanyCodes.length > 0 && (
                <div className="hr-company-dropdown">
                  {filteredCompanyCodes.map((company, index) => (
                    <div
                      key={`company-${company.code}-${index}`}
                      className="hr-company-option"
                      onClick={() => handleCompanySelect(company)}
                    >
                      <span className="hr-company-code">{company.code}</span>
                      <span className="hr-company-name">{company.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Fiscal Year */}
          <div className="filter-group">
            <label className="filter-label">Fiscal Year</label>
            <select 
              value={filters.fiscalYear}
              onChange={handleFiscalYearChange}
              className="filter-select"
            >
              <option value="All Fiscal Years">All Fiscal Years</option>
              {fiscalYears.map((year, index) => (
                <option key={`year-${year}-${index}`} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Posting Date */}
          <div className="filter-group">
            <label className="filter-label">Posting Date</label>
            <input 
              type="date"
              value={filters.postingDate}
              onChange={handlePostingDateChange}
              className="filter-input"
            />
          </div>

          {/* Apply Button */}
          <div className="filter-group">
            <label className="filter-label" style={{ opacity: 0 }}>Apply</label>
            <button 
              onClick={handleApplyFilters}
              disabled={!filters.orgId || loading}
              className="btn btn-apply"
              style={{ width: '100%', height: '42px' }}
            >
              {loading ? 'Applying...' : 'Apply Filters'}
            </button>
          </div>
        </div>
      </div>

      {/* Chart Controls Section */}
      <div className="hr-chart-controls-section">
  <div className="hr-chart-controls-card">
    <span className="hr-chart-controls-label">Select Charts to Display:</span>
    <button onClick={() => toggleChartVisibility('attendanceChart')} className={`hr-chart-toggle-btn ${chartVisibility.attendanceChart ? 'active' : ''}`}>
      {chartVisibility.attendanceChart ? <Eye size={16} /> : <EyeOff size={16} />}
      <Users size={16} /> Attendance
    </button>
    <button onClick={() => toggleChartVisibility('plannedLeaves')} className={`hr-chart-toggle-btn ${chartVisibility.plannedLeaves ? 'active' : ''}`}>
      {chartVisibility.plannedLeaves ? <Eye size={16} /> : <EyeOff size={16} />}
      <Calendar size={16} /> Planned Leaves
    </button>
    <button onClick={() => toggleChartVisibility('employeeTrend')} className={`hr-chart-toggle-btn ${chartVisibility.employeeTrend ? 'active' : ''}`}>
      {chartVisibility.employeeTrend ? <Eye size={16} /> : <EyeOff size={16} />}
      <TrendingUp size={16} /> Employee Trend
    </button>
    <button onClick={() => toggleChartVisibility('hiringExit')} className={`hr-chart-toggle-btn ${chartVisibility.hiringExit ? 'active' : ''}`}>
      {chartVisibility.hiringExit ? <Eye size={16} /> : <EyeOff size={16} />}
      <Users size={16} /> Hiring vs Exit
    </button>
    <button onClick={() => toggleChartVisibility('presentEmployees')} className={`hr-chart-toggle-btn ${chartVisibility.presentEmployees ? 'active' : ''}`}>
      {chartVisibility.presentEmployees ? <Eye size={16} /> : <EyeOff size={16} />}
      <Users size={16} /> Present Employees
    </button>
    <button onClick={() => toggleChartVisibility('onTimePerformance')} className={`hr-chart-toggle-btn ${chartVisibility.onTimePerformance ? 'active' : ''}`}>
      {chartVisibility.onTimePerformance ? <Eye size={16} /> : <EyeOff size={16} />}
      <Activity size={16} /> On-Time Performance
    </button>
    <button onClick={() => toggleChartVisibility('employeeDelay')} className={`hr-chart-toggle-btn ${chartVisibility.employeeDelay ? 'active' : ''}`}>
      {chartVisibility.employeeDelay ? <Eye size={16} /> : <EyeOff size={16} />}
      <Activity size={16} /> Employee Delay
    </button>
    {/* Add Birthday List toggle */}
    <button onClick={() => toggleChartVisibility('birthdayList')} className={`hr-chart-toggle-btn ${chartVisibility.birthdayList ? 'active' : ''}`}>
      {chartVisibility.birthdayList ? <Eye size={16} /> : <EyeOff size={16} />}
      🎂 Birthday List
    </button>
  </div>
</div>

      <div className="hr-main-layout">
        {loading ? (
          <div className="hr-loading-container">
            <div className="hr-loading-spinner"></div>
            <p>Loading employee data...</p>
          </div>
        ) : filters.orgId ? (
          <div className="hr-dashboard-content">
            {activeCategories.length > 0 ? (
              <>
                {/* Employee Distribution and Salary Overview */}
                <div className="hr-section-group">
                  <h3 className="hr-main-title">Employee Distribution</h3>
                  <div className={`hr-cards-row ${activeCategories.length <= 3 ? 'hr-centered' : ''}`}>
                    {activeCategories.map((category) => (
                      <HrCountCard
                        key={category.type}
                        title={category.title}
                        count={category.count}
                        type={category.type}
                        totalEmployees={employeeCounts.total}
                        icon={category.icon}
                      />
                    ))}
                  </div>
                </div>

                <SalaryCards />
<div className="hr-chart-compact">
  {chartVisibility.birthdayList && (
    <MonthlyBirthdayList      
      companyCode={filters.companyCode}
      isVisible={chartVisibility.birthdayList}
    />
  )}
</div>

                <div className="hr-charts-single-line">
                  <div className="hr-total-compact">
                    <TotalEmployeeBox />
                  </div>
                  <div className="hr-chart-compact">
                    <AttendanceChart />
                  </div>
                  <div className="hr-chart-compact">
                    <PlannedLeaveChart />
                  </div>
                  
                </div>

                {/* Charts with hide/show functionality */}
                {anyChartVisible && (
                  <>
                    <div className="hr-new-charts-section">
                      <EmployeeTrendChart />
                      <HiringExitChart />
                    </div>

                    <div className="hr-new-grid-section">
                      <PresentEmployees />
                      <OnTimePerformance />
                      <EmployeeDelayChart />
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="hr-no-data">
                <div className="hr-no-data-icon">📊</div>
                <h3>No Employee Data Found</h3>
                <p>
                  {filters.companyCode !== "" 
                    ? `No employee records found for Organization ${filters.orgId} and Company Code ${filters.companyCode}`
                    : `No employee records found for Organization ${filters.orgId}`
                  }
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="hr-empty-container">
            <div className="hr-empty-icon">🏢</div>
            <h3>No Organization Selected</h3>
            <p>Please select an organization to view employee data</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HrDashboardDropdownDynamic;